import React from "react";
import { Sparkles, Check, X, Send, Repeat, Rocket, Layout, Download, FileText } from "lucide-react";
import FeaturePage, { MockWindow, ScoreBar } from "./FeaturePage";
import { siblingLinks } from "./links";

const ACCENT = "#7C3AED";

// ── Hero visual: ATS score + keyword gaps ──
function HeroVisual() {
  return (
    <MockWindow label="RESUME ANALYSIS" badge="ATS 94" accent={ACCENT}>
      <div className="space-y-4">
        <ScoreBar label="ATS MATCH SCORE" value={94} accent={ACCENT} suffix="/100 · Strong" />
        <div className="rounded-xl border border-border p-3.5">
          <div className="font-mono text-[10px] tracking-[0.15em] mb-2.5" style={{ color: ACCENT }}>KEYWORD COVERAGE</div>
          <div className="flex flex-wrap gap-1.5">
            {[["GAAP", true], ["QuickBooks", true], ["Reconciliation", true], ["SOX", false], ["SAP", false]].map(([k, on]) => (
              <span key={k} className={`inline-flex items-center gap-1 font-mono text-[10px] px-2 py-1 rounded border ${
                on ? "border-[#10B981]/30 bg-[#10B981]/10 text-[#047857]" : "border-[#E11D48]/30 bg-[#E11D48]/10 text-[#E11D48]"
              }`}>
                {on ? <Check className="w-2.5 h-2.5" /> : <X className="w-2.5 h-2.5" />} {k}
              </span>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-1.5 font-mono text-[10px] text-muted-foreground">
          <Sparkles className="w-3 h-3" style={{ color: ACCENT }} /> 2 missing keywords the recruiter's ATS filters for
        </div>
      </div>
    </MockWindow>
  );
}

// ── Row visual: before/after rewrite ──
function RewriteVisual() {
  return (
    <MockWindow label="AI REWRITE" badge="58 → 94" accent={ACCENT}>
      <div className="space-y-3">
        <div className="rounded-xl border p-3.5" style={{ borderColor: "#E11D4833", background: "#E11D480D" }}>
          <div className="font-mono text-[10px] tracking-[0.15em] mb-1.5 text-[#E11D48]">BEFORE · SCORE 58</div>
          <p className="text-xs text-muted-foreground leading-relaxed line-through/0">
            "Responsible for month-end close and account reconciliations."
          </p>
        </div>
        <div className="flex justify-center"><span className="font-mono text-[10px] text-muted-foreground">↓ rewritten</span></div>
        <div className="rounded-xl border p-3.5" style={{ borderColor: `${ACCENT}33`, background: `${ACCENT}0D` }}>
          <div className="font-mono text-[10px] tracking-[0.15em] mb-1.5" style={{ color: ACCENT }}>AFTER · SCORE 94</div>
          <p className="text-xs text-[#0F172A] leading-relaxed">
            "Owned month-end close for a $40M P&L, cutting close time from 8 to 3 days and clearing a 200-item reconciliation backlog."
          </p>
        </div>
      </div>
    </MockWindow>
  );
}

// ── Row visual: formatting checklist ──
function FormatVisual() {
  const checks = [
    ["Single-column, ATS-safe layout", true],
    ["No tables or text boxes", true],
    ["Standard section headings", true],
    ["Quantified impact in bullets", false],
    ["Consistent date formatting", true],
  ];
  return (
    <MockWindow label="FORMATTING CHECK" accent={ACCENT}>
      <div className="space-y-2">
        {checks.map(([t, ok]) => (
          <div key={t} className="flex items-center gap-2.5 rounded-lg border border-border p-2.5">
            <div className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 ${
              ok ? "bg-[#10B981]/15 text-[#047857]" : "bg-[#F59E0B]/15 text-[#B45309]"
            }`}>
              {ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
            </div>
            <span className="text-xs text-[#0F172A]">{t}</span>
          </div>
        ))}
      </div>
    </MockWindow>
  );
}

// ── Row visual: template picker with live preview + downloads ──
function TemplatesVisual() {
  // Distinct mini-layouts so the three templates read differently at a glance.
  const AtsThumb = () => (
    <div className="space-y-1 p-2">
      <div className="h-1.5 w-2/3 mx-auto rounded-full bg-[#0F172A]/70" />
      <div className="h-0.5 w-1/2 mx-auto rounded-full bg-[#0F172A]/20" />
      <div className="pt-1 space-y-0.5">{[1, 1, 1, 1].map((_, i) => <div key={i} className="h-0.5 w-full rounded-full bg-[#0F172A]/15" />)}</div>
    </div>
  );
  const ProThumb = () => (
    <div className="p-1.5">
      <div className="h-3 w-full rounded-sm mb-1" style={{ background: `${ACCENT}CC` }} />
      <div className="space-y-0.5">{[1, 1, 1, 1, 1].map((_, i) => <div key={i} className="h-0.5 w-full rounded-full bg-[#0F172A]/15" />)}</div>
    </div>
  );
  const ModernThumb = () => (
    <div className="flex gap-1 p-1.5 h-full">
      <div className="w-1/3 rounded-sm" style={{ background: `${ACCENT}22` }} />
      <div className="flex-1 space-y-0.5 pt-0.5">{[1, 1, 1, 1, 1].map((_, i) => <div key={i} className="h-0.5 w-full rounded-full bg-[#0F172A]/15" />)}</div>
    </div>
  );
  const templates = [
    { label: "ATS Safe", Thumb: AtsThumb, sel: true, free: true },
    { label: "Professional", Thumb: ProThumb, sel: false, free: false },
    { label: "Modern", Thumb: ModernThumb, sel: false, free: false },
  ];
  return (
    <MockWindow label="RESUME TEMPLATES" badge="Live preview" accent={ACCENT}>
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-2.5">
          {templates.map((t) => (
            <div key={t.label} className="text-center">
              <div className="rounded-lg border-2 bg-white h-20 overflow-hidden mb-1.5"
                style={{ borderColor: t.sel ? ACCENT : "#E5E7EB" }}>
                <t.Thumb />
              </div>
              <div className="flex items-center justify-center gap-1 font-mono text-[9px] text-[#0F172A]">
                {t.sel && <Check className="w-2.5 h-2.5" style={{ color: ACCENT }} />}
                {t.label}
              </div>
              <div className="font-mono text-[8px] text-muted-foreground">{t.free ? "Free" : "Premium"}</div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="flex items-center justify-center gap-1.5 rounded-xl px-3 py-2.5 text-xs font-bold text-white" style={{ background: ACCENT }}>
            <Download className="w-3.5 h-3.5" /> Download PDF
          </div>
          <div className="flex items-center justify-center gap-1.5 rounded-xl border px-3 py-2.5 text-xs font-bold" style={{ borderColor: `${ACCENT}40`, color: ACCENT }}>
            <FileText className="w-3.5 h-3.5" /> Download DOCX
          </div>
        </div>
      </div>
    </MockWindow>
  );
}

export default function ResumeAnalyzerFeature() {
  return (
    <FeaturePage
      accent={ACCENT}
      eyebrow="RESUME ANALYZER"
      title="Beat the bots."
      titleAccent="Then beat the humans."
      subtitle="Paste your resume and a job description to get an instant ATS match score, the exact keywords you're missing, a formatting check — and an AI rewrite that scores higher. Stop guessing why you're not getting callbacks."
      heroChips={["Instant ATS score", "One-click AI rewrite", "PDF & DOCX templates"]}
      heroVisual={<HeroVisual />}
      rows={[
        {
          tag: "ATS SCORE",
          title: "See your resume the way the software does.",
          body: "Most applications are filtered by an ATS before a human ever reads them. We score your resume 0–100 against the specific job description, so you know if you'd even make it to the recruiter's desk.",
          bullets: [
            "0–100 match score per job",
            "Missing keywords the ATS filters for",
            "Prioritised by impact on your score",
          ],
          visual: <HeroVisual />,
        },
        {
          tag: "AI REWRITE",
          title: "Weak bullets, rewritten to win.",
          body: "Vague, duty-based bullets tank your score. The AI rewrites them into quantified, keyword-rich achievements that read well to both the ATS and the hiring manager — and it shows you the score jump.",
          bullets: [
            "Bullet-by-bullet rewrite suggestions",
            "Quantified, achievement-focused phrasing",
            "Watch your score climb in real time",
          ],
          visual: <RewriteVisual />,
          reverse: true,
        },
        {
          tag: "FORMATTING CHECK",
          title: "Catch the silent rejections.",
          body: "Tables, text boxes, and creative layouts quietly break ATS parsing. We flag the formatting traps that get resumes auto-rejected — so a great candidate never loses to a bad export.",
          bullets: [
            "ATS-safe layout validation",
            "Section + date consistency checks",
            "Export a clean, parseable version",
          ],
          visual: <FormatVisual />,
        },
        {
          tag: "RESUME TEMPLATES",
          title: "Look the part — in one click.",
          body: "Once your resume is optimised, drop it into a professionally designed template, preview it live, and download a polished PDF or DOCX. Start with the free ATS-safe layout, or switch to Professional and Modern designs.",
          bullets: [
            "ATS Safe, Professional & Modern templates",
            "Live preview before you download",
            "Export to PDF or DOCX in one click",
          ],
          visual: <TemplatesVisual />,
          reverse: true,
        },
      ]}
      steps={[
        { title: "Paste resume + job", body: "Drop in your resume and the job description you're targeting." },
        { title: "Get your score & gaps", body: "See your ATS score, missing keywords, and formatting flags instantly." },
        { title: "Apply the AI rewrite", body: "Accept stronger bullets and re-score until you're callback-ready." },
      ]}
      useCases={[
        { icon: Send, persona: "High-volume applicants", body: "Tailor and re-score your resume per job in seconds, so every application clears the ATS filter." },
        { icon: Repeat, persona: "Career switchers", body: "Surface the keywords a new field expects and rewrite bullets to speak that industry's language." },
        { icon: Rocket, persona: "Anyone not getting callbacks", body: "Find out exactly why you're being filtered out — and fix it — instead of guessing." },
      ]}
      faqs={[
        { q: "What is an ATS score?", a: "It's a 0–100 measure of how well your resume matches a specific job description the way applicant-tracking software reads it. A higher score means you're more likely to pass the automated filter and reach a human." },
        { q: "Do I need a job description?", a: "For the most accurate score, yes — paste the job you're targeting so we can compare against its exact keywords. You still get formatting and general feedback without one." },
        { q: "What does the AI rewrite change?", a: "It turns vague, duty-based bullets into quantified, keyword-rich achievements that read well to both the ATS and the hiring manager, and shows you the score improvement." },
        { q: "Why would formatting reject my resume?", a: "Tables, text boxes, and multi-column layouts often break ATS parsing, so a strong candidate gets silently filtered. We flag those traps and can export a clean, parseable version." },
        { q: "Do you offer resume templates?", a: "Yes. After your resume is optimised, you can drop it into a professionally designed template — ATS Safe, Professional, or Modern — preview it live, and download it as a PDF or DOCX. The ATS Safe template is free." },
        { q: "Can I check more than one resume?", a: "Yes — analyse and save multiple versions, and re-score any of them against different jobs." },
      ]}
      ctaTitle="Get more callbacks."
      ctaSubtitle="Free to start. Score your resume in seconds."
      related={siblingLinks("resume-analyzer")}
      seoTitle="Resume Analyzer — ATS Score, Keyword Gaps & AI Rewrite | VetraHub"
      seoDescription="Score your resume against any job description, find missing keywords, fix ATS-breaking formatting, get an AI rewrite that scores higher, and download it in a professional PDF or DOCX template. Free to start."
    />
  );
}
